// src/shared/ui/overlay/HoverPopover.jsx

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * @typedef {Object} HoverPopoverPosition
 * @property {number} top  - Popover top coordinate in viewport pixels (used with `position: fixed`).
 * @property {number} left - Popover left coordinate in viewport pixels (used with `position: fixed`).
 */

/**
 * HoverPopover
 *
 * Renders a lightweight hover/focus popover using a React portal (document.body) so it
 * won't be clipped by overflow containers (e.g., tables with overflow-x-auto).
 *
 * Interaction model:
 * - Opens on mouse enter or focus.
 * - Closes on mouse leave or blur after a short delay (to allow pointer travel).
 * - Keeps open while hovering the popover itself.
 *
 * Positioning model:
 * - Anchors off the trigger element's bounding rect.
 * - Places popover to the right of the trigger with a vertical nudge.
 * - Uses `position: fixed` and clamps into the viewport once the popover is measurable.
 *
 * Notes:
 * - When `disabled` is true, the component renders only the trigger wrapper and never portals.
 * - The trigger wrapper is focusable via tabIndex=0 to support keyboard access.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Trigger content that the user hovers/focuses.
 * @param {React.ReactNode} props.content - Popover body content rendered in the portal.
 * @param {number} [props.offset=10] - Horizontal offset (px) from the trigger’s right edge.
 * @param {number|string} [props.maxWidth=420] - Max width applied to the popover container.
 * @param {string} [props.popoverClassName=""] - Additional className(s) appended to the popover container.
 * @param {boolean} [props.disabled=false] - If true, disables all popover behavior and rendering.
 * @returns {JSX.Element}
 */
export function HoverPopover({
  children,
  content,
  offset = 10,
  maxWidth = 420,
  popoverClassName = "",
  disabled = false,
}) {
  /**
   * Reference to the trigger wrapper element.
   * Used as the anchor for computePosition().
   *
   * @type {React.MutableRefObject<HTMLSpanElement|null>}
   */
  const triggerRef = useRef(null);

  /**
   * Reference to the popover container element.
   * Used to measure dimensions for viewport clamping.
   *
   * @type {React.MutableRefObject<HTMLDivElement|null>}
   */
  const popoverRef = useRef(null);

  /**
   * Timer handle for deferred close behavior.
   * Stored in a ref so it persists across renders.
   *
   * @type {React.MutableRefObject<number|null>}
   */
  const closeTimerRef = useRef(null);

  /**
   * Whether the popover is currently open.
   *
   * @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]}
   */
  const [open, setOpen] = useState(false);

  /**
   * Current popover position in viewport coordinates for `position: fixed`.
   *
   * @type {[HoverPopoverPosition, React.Dispatch<React.SetStateAction<HoverPopoverPosition>>]}
   */
  const [pos, setPos] = useState({ top: 0, left: 0 });

  /**
   * Clear any pending close timer.
   *
   * @returns {void}
   */
  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  /**
   * Schedule popover close after a short delay.
   * The delay supports moving the pointer from trigger -> popover without flicker.
   *
   * @returns {void}
   */
  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  };

  /**
   * Compute initial popover position relative to the trigger element.
   * - Places the popover to the right of the trigger (`offset` px).
   * - Adds a small vertical nudge so it aligns pleasantly with the trigger.
   *
   * Uses viewport coordinates (via getBoundingClientRect) since the popover is `fixed`.
   *
   * @returns {void}
   */
  const computePosition = () => {
    const el = triggerRef.current;
    if (!el) return;

    const r = el.getBoundingClientRect();

    // Start by placing to the right of the icon, vertically aligned near it.
    const initialTop = r.top + 12; // nudge down slightly
    const initialLeft = r.right + offset;

    setPos({ top: initialTop, left: initialLeft });
  };

  /**
   * While open, reposition on scroll/resize to keep the popover anchored to the trigger.
   * Uses capture phase for scroll to handle nested scroll containers.
   */
  useEffect(() => {
    if (!open) return;

    const onScroll = () => computePosition();
    const onResize = () => computePosition();

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  /**
   * After opening, compute position and clamp the popover within the viewport.
   * useLayoutEffect ensures measurements occur after DOM layout, before paint.
   *
   * Clamping:
   * - Keeps the popover within a padded viewport box.
   * - Adjusts left/top if the popover would overflow right/bottom.
   * - Ensures top isn't above the padding.
   *
   * Note: This effect intentionally depends only on `open` (and suppresses exhaustive-deps)
   * to preserve existing behavior and avoid extra renders due to pos changes.
   */
  useLayoutEffect(() => {
    if (!open) return;

    computePosition();

    // Clamp into viewport once the popover is measurable
    const pr = popoverRef.current?.getBoundingClientRect();
    if (!pr) return;

    const pad = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = pos.left;
    let top = pos.top;

    if (left + pr.width > vw - pad) left = Math.max(pad, vw - pr.width - pad);
    if (top + pr.height > vh - pad) top = Math.max(pad, vh - pr.height - pad);
    if (top < pad) top = pad;

    if (left !== pos.left || top !== pos.top) setPos({ top, left });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * Cleanup on unmount: ensure we do not leave timers running.
   */
  useEffect(() => {
    return () => clearCloseTimer();
  }, []);

  /**
   * Disabled mode: render only the trigger wrapper and do not attach handlers/portal.
   */
  if (disabled) {
    return <span ref={triggerRef}>{children}</span>;
  }

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex items-center"
        onMouseEnter={() => {
          clearCloseTimer();
          computePosition();
          setOpen(true);
        }}
        onMouseLeave={scheduleClose}
        onFocus={() => {
          clearCloseTimer();
          computePosition();
          setOpen(true);
        }}
        onBlur={scheduleClose}
        onKeyDown={event => {
          if (event.key === 'Escape') {
            clearCloseTimer();
            setOpen(false);
            event.stopPropagation();
          }
        }}
        tabIndex={0}
      >
        {children}
      </span>

      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={popoverRef}
            className={`fixed z-[9999] rounded-md border border-gray-200 bg-white shadow-lg ${popoverClassName}`}
            style={{
              top: pos.top,
              left: pos.left,
              maxWidth,
            }}
            onMouseEnter={() => {
              clearCloseTimer();
              setOpen(true);
            }}
            onMouseLeave={scheduleClose}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
}
